/**
 * Why Members Joined, by Age
 *
 * Stacked bar of new members per onboarding age bucket, split by the four
 * join-reason themes. Source: users.onboarding_age_range x
 * users.onboarding_motivation, filtered on users.created_at (i.e. members who
 * JOINED in the selected window).
 *
 * Self-contained: owns its range state and its own fetch, so it does not
 * disturb the Redis-cached /platform-stats payload the rest of this page uses.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Paper, Typography } from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import apiService, {
  OnboardingBreakdownResponse,
  OnboardingBreakdownRow,
} from '../../services/api';
import TimeRangeFilter from '../common/TimeRangeFilter';
import { TimeRangeToken, describeRange, resolveRange } from '../common/timeRange';
import { useTimeRangeParam } from '../common/useTimeRangeParam';
import {
  APP_AGE_ORDER,
  APP_THEMES,
  ONBOARDING_LABELS,
  themeKeyFor,
} from '../common/onboardingThemes';

type ChartRow = { name: string } & Record<string, string | number>;

/** Pivots the flat cross-tab into one row per age bucket, themes as columns. */
function pivot(rows: OnboardingBreakdownRow[]): ChartRow[] {
  return APP_AGE_ORDER.map((age) => {
    const row: ChartRow = { name: ONBOARDING_LABELS[age] || age };
    APP_THEMES.forEach((theme) => {
      row[theme.key] = 0;
    });
    rows
      .filter((r) => r.age_range === age)
      .forEach((r) => {
        const key = themeKeyFor(APP_THEMES, r.motivation);
        if (key) row[key] = (row[key] as number) + r.count;
      });
    return row;
  });
}

const JoinReasonByAgeChart: React.FC = () => {
  // Prefixed: this tab hosts several independent panels, and the range must not
  // collide with any other filter that later lands in the same URL.
  const [range, setRange] = useTimeRangeParam<TimeRangeToken>(
    { type: '90d' },
    { prefix: 'joinreason' }
  );
  const [data, setData] = useState<OnboardingBreakdownResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiService.getOnboardingBreakdown(resolveRange(range));
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load onboarding breakdown');
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    load();
  }, [load]);

  const chartData = data ? pivot(data.breakdown) : [];
  const answered = data?.answered_total ?? 0;

  return (
    <Paper
      elevation={0}
      sx={{ p: 3, borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1.5,
          mb: 2,
        }}
      >
        <Typography
          sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '14px', fontWeight: 700 }}
        >
          Why Members Joined, by Age
        </Typography>
        <TimeRangeFilter
          value={range}
          onChange={setRange}
          onRefresh={load}
          refreshing={loading}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ height: 280, minHeight: 280 }}>
        {loading ? (
          <Box
            sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <CircularProgress size={28} />
          </Box>
        ) : answered === 0 ? (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: 0.5,
            }}
          >
            <Typography
              sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13px', fontWeight: 600 }}
            >
              No questionnaire answers in this range
            </Typography>
            <Typography
              sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '12px', color: 'text.secondary' }}
            >
              Try a wider date range.
            </Typography>
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <RechartsTooltip />
              <Legend wrapperStyle={{ fontSize: '11px' }} />
              {/* fill goes on the Bar itself — recharts v3 renders empty rects
                  when bars are coloured with <Cell> children (see FunnelChart). */}
              {APP_THEMES.map((theme) => (
                <Bar
                  key={theme.key}
                  dataKey={theme.key}
                  name={theme.label}
                  stackId="a"
                  fill={theme.color}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </Box>

      {!loading && (
        <Typography
          sx={{
            fontFamily: "'Montserrat', sans-serif",
            fontSize: '11px',
            color: 'text.secondary',
            mt: 1.5,
          }}
        >
          {answered} of {data?.members_total ?? 0} members who joined in {describeRange(range)}{' '}
          answered the onboarding questionnaire.
        </Typography>
      )}
    </Paper>
  );
};

export default JoinReasonByAgeChart;
